import { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { TabPanels, Tabs } from "@reach/tabs";
import { NumberParam, useQueryParams } from "use-query-params";
import {
  ContentWrapper,
  FullWidthHorizontalSeparator,
  PageHeader,
  PageTitle,
  PageWrapper,
} from "../../../../../layout/portalPageLayout";
import {
  HeaderLeft,
  HeaderRight,
} from "../../../../../components/Layout/Layout";
import { DestructiveButtonSmallV2 } from "../../../../../components/Buttons/Buttons";
import { LazyTabPanel } from "../../../../../components/ReachTabs/TabPanel";
import { TabList } from "../../../../../components/ReachTabs/TabList";
import { Tab } from "../../../../../components/ReachTabs/Tab";
import { useRoutePath } from "../../../../../util/Routing";
import type { EntityType } from "../../../../../types/types.MDM";
import { useMdmEntityDisplayNames } from "../../../../../util/mdmEntityDisplayNames";
import { EntityCreationForm } from "./CreateEntityPage";
import { CreateEntityFromUploads } from "./CreateFromUploads/CreateEntityFromUploads";

type CreateEntityTabPageProps = {
  entityType: EntityType;
};

export function CreateEntityTabPage({
  entityType,
}: CreateEntityTabPageProps): JSX.Element {
  const { t } = useTranslation();
  const { adminPath } = useRoutePath();
  const history = useHistory();
  const [currentTab, setCurrentTab] = useState<number>(0);
  const [query, setQuery] = useQueryParams({ tab: NumberParam });

  const { getLabel } = useMdmEntityDisplayNames();
  const entityLabel = getLabel(entityType, "singular");
  const listPath = `${adminPath}/mdm/${entityType}s`;

  const changeTab = useCallback(
    (index: number) => {
      setCurrentTab(index);
      setQuery({ tab: index });
    },
    [setQuery]
  );

  useEffect(() => {
    if (query.tab) {
      changeTab(query.tab);
    }
  }, [changeTab, query]);

  return (
    <PageWrapper>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>{t("Create {{label}}", { label: entityLabel })}</PageTitle>
        </HeaderLeft>
        <HeaderRight>
          <DestructiveButtonSmallV2 onClick={() => history.push(listPath)}>
            {t("Cancel")}
          </DestructiveButtonSmallV2>
        </HeaderRight>
      </PageHeader>
      <ContentWrapper>
        <Tabs onChange={changeTab} index={currentTab}>
          <TabList>
            <Tab key="create_entity">
              {t("New {{label}}", { label: entityLabel })}
            </Tab>
            <Tab key="bulk_upload">{t("Bulk Upload")}</Tab>
          </TabList>
          <FullWidthHorizontalSeparator />
          <TabPanels>
            <LazyTabPanel
              key="create_entity"
              refreshKey={currentTab}
              isSelected={currentTab === 0}
            >
              <EntityCreationForm
                entityType={entityType}
                onCancel={() => history.push(listPath)}
              />
            </LazyTabPanel>
            <LazyTabPanel
              key="bulk_upload"
              refreshKey={currentTab}
              isSelected={currentTab === 1}
            >
              <CreateEntityFromUploads entityType={entityType} />
            </LazyTabPanel>
          </TabPanels>
        </Tabs>
      </ContentWrapper>
    </PageWrapper>
  );
}
